import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import posthog from 'posthog-js';
import OrderForm from '../../components/orders/OrderForm';
import Alert from '../../components/common/Alert';
import useOrders from '../../hooks/useOrders';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import useRequireCompleteProfile from '../../hooks/useRequireCompleteProfile';
import { getAddresses } from '../../services/customers';
import { getSessions } from '../../services/sessions';
import { getCreditBalance } from '../../services/auth';
import './NewOrder.css';

/**
 * NewOrder page - checkout and place order
 */
const NewOrder = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  useRequireCompleteProfile();
  const { createOrder, isCreating } = useOrders({ autoFetch: false });
  const { items, updateItem, removeItem, clearCart } = useCart();

  const [submitError, setSubmitError] = useState(null);
  const checkoutTracked = useRef(false);
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [creditBalanceCents, setCreditBalanceCents] = useState(0);

  // Fetch company shipping addresses on mount
  useEffect(() => {
    if (!profile?.company_id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAddressesLoading(true);
    getAddresses(profile.company_id)
      .then((data) => {
        // Support both paginated { results: [] } and plain array responses
        const all = Array.isArray(data) ? data : (data.results ?? []);
        setAddresses(all.filter((a) => a.address_type === 'shipping'));
      })
      .catch(() => setAddresses([]))
      .finally(() => setAddressesLoading(false));
  }, [profile?.company_id]);

  useEffect(() => {
    getCreditBalance()
      .then((data) => setCreditBalanceCents(data.credit_balance_cents || 0))
      .catch(() => {});
  }, []);

  // Fetch open mill sessions on mount (backend filters to mill_end >= today for customers)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessionsLoading(true);
    getSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = async (formData) => {
    try {
      setSubmitError(null);

      // Prepare order data for API
      const isPickup = formData.fulfillment_type === 'pickup';
      const orderData = {
        company: profile.company_id,
        fulfillment_type: formData.fulfillment_type,
        ...(formData.session_id != null ? { session: formData.session_id } : {}),
        special_instructions: formData.special_instructions || '',
        // Only include delivery_address for delivery orders
        ...(!isPickup && formData.delivery_address_id
          ? { delivery_address: formData.delivery_address_id }
          : {}),
        items: items.map(item => ({
          product_variant_id: item.productVariantId,
          quantity: item.quantity,
          ...(item.is_sample ? { is_sample: true } : {}),
        })),
      };

      // Create the order
      const createdOrder = await createOrder(orderData);

      posthog.capture('order_placed', {
        order_id: createdOrder.id,
        order_number: createdOrder.order_number,
        fulfillment_type: formData.fulfillment_type,
        item_count: items.reduce((s, i) => s + i.quantity, 0),
        products: items.map(i => i.product?.name).filter(Boolean),
      });

      // Clear the cart and navigate to order detail page
      clearCart();
      navigate(`/orders/${createdOrder.id}`, {
        state: { successMessage: 'Order placed successfully!' },
      });
    } catch (error) {
      console.error('Failed to create order:', error);
      setSubmitError(
        error.message || 'Failed to place order. Please try again.'
      );

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const handleUpdateItemQuantity = (itemId, newQuantity) => {
    updateItem(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId) => {
    removeItem(itemId);
    // items.length here is stale (React batches state), so navigation when
    // the last item is removed is handled by the effect below instead.
  };

  // Fire checkout_started once when the cart is confirmed non-empty
  useEffect(() => {
    if (items.length > 0 && !checkoutTracked.current) {
      checkoutTracked.current = true;
      posthog.capture('checkout_started', {
        item_count: items.reduce((s, i) => s + i.quantity, 0),
        products: items.map(i => i.product?.name).filter(Boolean),
      });
    }
  }, [items]);

  // Navigate away when the cart becomes empty after the form has mounted.
  // This is the correct place to check post-removal length.
  useEffect(() => {
    if (items.length === 0) {
      navigate('/products');
    }
  }, [items.length, navigate]);

  // Redirect if no items
  if (items.length === 0) {
    return (
      <div className="new-order-page new-order-page--empty">
        <div className="new-order-page__empty-state">
          <Alert type="warning">
            Your cart is empty. Add products to your order first.
          </Alert>
          <button
            className="new-order-page__back-button"
            onClick={() => navigate('/products')}
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-order-page">
      {/* Header */}
      <div className="new-order-page__header">
        <Link to="/products" className="new-order-page__back">← Products</Link>
        <p className="new-order-page__eyebrow">Deep Roots Milling</p>
        <h1 className="new-order-page__title">Place Your Order</h1>
        <hr className="new-order-page__rule" />
      </div>

      {/* Error Alert */}
      {submitError && (
        <div className="new-order-page__alert">
          <Alert type="error">{submitError}</Alert>
        </div>
      )}

      {/* Order Form */}
      <div className="new-order-page__content">
        <OrderForm
          items={items}
          addresses={addresses}
          addressesLoading={addressesLoading}
          sessions={sessions}
          sessionsLoading={sessionsLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onUpdateItem={handleUpdateItemQuantity}
          onRemoveItem={handleRemoveItem}
          isSubmitting={isCreating}
          creditBalanceCents={creditBalanceCents}
        />
      </div>
    </div>
  );
};

export default NewOrder;
